import React from 'react';
import { SummaryReferral } from '../../types/visit-summary.types';
import { useI18n } from '../../hooks/useI18n';

interface ReferralModalProps {
  referral: SummaryReferral | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ReferralModal: React.FC<ReferralModalProps> = ({
  referral,
  isOpen,
  onClose,
}) => {
  const { t } = useI18n();

  if (!isOpen || !referral) return null;

  const hasCode = !!referral.ESkierowanieCode;
  const hasDiseases = Array.isArray(referral.Diseases) && referral.Diseases.length > 0;
  const hasProcedures = Array.isArray(referral.Procedures) && referral.Procedures.length > 0;
  const hasReason = !!referral.Reason;
  const hasNotes = !!referral.AdditionalNotes;

  return (
    <div className="cab-rec-overlay" onClick={onClose}>
      <div className="cab-rec-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cab-rec-header">
          <div>
            <h2 className="cab-rec-title">{t('doc_referral_title')}</h2>
            <div className="cab-rec-code">
              {hasCode && (
                <>
                  <span className="cab-rec-code-label">{t('doc_referral_code')}:</span>{' '}
                  <span className="cab-rec-code-value">{referral.ESkierowanieCode}</span>
                </>
              )}
            </div>
            {referral.CreatedAt && (
              <p className="cab-rec-date">{referral.CreatedAt.substring(0, 10)}</p>
            )}
          </div>
          <button className="cab-rec-close" onClick={onClose} aria-label={t('doc_close')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="cab-rec-body">
          {/* Тип скерування */}
          {referral.TypeName && (
            <div className="cab-rf-section">
              <span className="cab-rf-label">{t('doc_referral_type')}</span>
              <div className="cab-rf-type-row">
                <span className="cab-rf-type-badge">{referral.TypeName}</span>
                {referral.IsUrgent && (
                  <span className="cab-rf-urgent-badge">{t('doc_referral_urgent')}</span>
                )}
              </div>
            </div>
          )}

          {/* Список діагнозів */}
          {hasDiseases && (
            <div className="cab-rf-section">
              <span className="cab-rf-label">{t('doc_referral_diseases')}</span>
              <div className="cab-rx-list">
                {referral.Diseases.map((disease, idx) => (
                  <div key={idx} className="cab-rx-card">
                    <div className="cab-rx-card-top">
                      <span className="cab-rx-name">{disease.FullName}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Список процедур/досліджень */}
          {hasProcedures && (
            <div className="cab-rf-section">
              <span className="cab-rf-label">{t('doc_referral_procedures')}</span>
              <div className="cab-rx-list">
                {referral.Procedures.map((proc, idx) => (
                  <div key={idx} className="cab-rx-card">
                    <div className="cab-rx-card-top">
                      <span className="cab-rx-name">{proc.FullName}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Причина скерування */}
          {hasReason && (
            <div className="cab-rf-section">
              <span className="cab-rf-label">{t('doc_referral_reason')}</span>
              <p className="cab-rf-text">{referral.Reason}</p>
            </div>
          )}

          {/* Додаткові примітки */}
          {hasNotes && (
            <div className="cab-rf-section">
              <span className="cab-rf-label">{t('doc_referral_notes')}</span>
              <p className="cab-rf-text">{referral.AdditionalNotes}</p>
            </div>
          )}

          {/* Порожній стан */}
          {!hasDiseases && !hasProcedures && !hasReason && !hasNotes && !referral.TypeName && (
            <p className="cab-rec-empty">{t('doc_referral_empty')}</p>
          )}
        </div>
      </div>

      <style>{`
        .cab-rec-overlay {
          position: fixed;
          inset: 0;
          background: rgba(30, 10, 60, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 9999;
          padding: 0 16px;
          animation: cab-rec-fade-in 0.2s ease;
        }
        @keyframes cab-rec-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .cab-rec-modal {
          background: #fff;
          border-radius: 24px 24px 0 0;
          width: 100%;
          max-width: 680px;
          max-height: 90dvh;
          display: flex;
          flex-direction: column;
          animation: cab-rec-slide-up 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 -10px 60px rgba(54, 23, 119, 0.15);
        }
        @keyframes cab-rec-slide-up {
          from { transform: translateY(60px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (min-width: 600px) {
          .cab-rec-overlay {
            align-items: center;
            padding: 20px;
          }
          .cab-rec-modal {
            border-radius: 24px;
            max-height: 85vh;
          }
        }
        .cab-rec-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 24px 20px 16px 20px;
          background: #fff;
          border-radius: 24px 24px 0 0;
          flex-shrink: 0;
        }
        .cab-rec-title {
          font-family: 'Poppins', sans-serif;
          font-size: 24px;
          font-weight: 600;
          color: var(--cab-violet, #361777);
          margin: 0 0 4px;
          line-height: 1.2;
        }
        .cab-rec-code {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: #786798;
          margin: 0 0 4px;
        }
        .cab-rec-code-label {
          font-weight: 600;
        }
        .cab-rec-code-value {
          font-weight: 600;
        }
        .cab-rec-date {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: var(--cab-note, #786798);
          margin: 0;
        }
        .cab-rec-close {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: #F4F0FA;
          color: var(--cab-violet, #361777);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .cab-rec-close svg {
          width: 20px;
          height: 20px;
        }
        .cab-rec-close:hover { background: #E6D6FA; }
        .cab-rec-body {
          flex: 1;
          overflow-y: auto;
          padding: 0 20px 24px 20px;
          overscroll-behavior: contain;
        }
        .cab-rec-empty {
          text-align: center;
          color: var(--cab-note, #786798);
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          padding: 40px 0;
        }
        .cab-rf-section {
          margin-bottom: 20px;
        }
        .cab-rf-label {
          display: block;
          font-family: 'Poppins', sans-serif;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          color: var(--cab-violet, #361777);
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #E6D6FA;
        }
        .cab-rf-type-row {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .cab-rf-type-badge {
          background: #E6D6FA;
          color: #361777;
          padding: 6px 16px;
          border-radius: 100px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 14px;
        }
        .cab-rf-urgent-badge {
          background: #FEE2E2;
          color: #DC2626;
          padding: 4px 12px;
          border-radius: 100px;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 12px;
        }
        .cab-rf-text {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #333;
          margin: 0;
          background: #F9F7FF;
          border-radius: 12px;
          padding: 12px 16px;
        }
        .cab-rx-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .cab-rx-card {
          background: #F4F4F4;
          border-radius: 16px;
          padding: 14px 16px;
        }
        .cab-rx-card-top {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          color: #333;
        }
        .cab-rx-name {
          font-weight: 500;
          color: #333;
          line-height: 1.5;
        }
        @media (min-width: 600px) {
          .cab-rec-header {
            padding: 32px 45px 16px 45px;
          }
          .cab-rec-title {
            font-size: 24px;
          }
          .cab-rec-close {
            width: 44px;
            height: 44px;
          }
          .cab-rec-close svg {
            width: 24px;
            height: 24px;
          }
          .cab-rec-body {
            padding: 0 45px 32px 45px;
          }
          .cab-rx-card {
            padding: 16px;
          }
          .cab-rx-card-top {
            font-size: 15px;
          }
        }
      `}</style>
    </div>
  );
};
